// A roster's slots as numbers to set: every starting slot and the bench the
// sport has, the injured-reserve spots, and the taxi squad where its caller
// offers one. League settings' Roster section and the create form's More
// options draw the same part (PLAN-2026-10-18-families-and-newcomers §4.6).
//
// The sport names the slots (`SportDefinition.slots`); this part names none.

import type { SportDefinition } from '@fsp/sports-core';
import { Caption, Choice, NumberField, Stack } from '../../kit/ui';

/** A taxi squad's rule as the part sets it; its slot is the sport's. */
export interface TaxiCounts {
  count: number;
  maxYearsExperience: number;
  /** The week whose first kickoff closes it; null is the season's first. */
  deadlinePeriod: number | null;
}

export interface RosterCounts {
  slots: readonly { slot: string; count: number }[];
  /** Injured-reserve spots; 0 is none. */
  reserve: number;
}

export function RosterSlots({
  roster,
  sport,
  offered,
  disabled = false,
  onSlot,
  onReserve,
  taxi,
}: {
  roster: RosterCounts;
  sport: SportDefinition;
  /** The spots to offer, when not every one (`rosterSlotsOffered`); a spot in use always shows. */
  offered?: readonly string[];
  disabled?: boolean;
  onSlot: (slot: string, count: number) => void;
  onReserve: (count: number) => void;
  /**
   * The taxi squad (PLAN-2026-10-27-the-next-season §2.7), offered when the
   * caller passes it and the sport has a taxi slot: its rule, the regular
   * season's weeks it may close in, and the rule changed, null for none.
   */
  taxi?: {
    rule: TaxiCounts | null;
    weeks: { first: number; last: number };
    onChange: (rule: TaxiCounts | null) => void;
  };
}) {
  const kindOf = (slot: string) => sport.slots.find((x) => x.id === slot)?.kind;
  const starters = roster.slots
    .filter((sl) => kindOf(sl.slot) === 'starter')
    .reduce((n, sl) => n + sl.count, 0);
  // THE TAXI SQUAD IS NOT A ROSTER SPOT: it counts toward no limit, as the
  // lineup's "15 of 15 roster spots" and the validator's draft rounds read.
  const total = roster.slots.reduce((n, sl) => n + sl.count, 0) + roster.reserve;
  const reserve = sport.slots.find((sl) => sl.kind === 'reserve');
  const taxiSlot = sport.slots.find((sl) => sl.kind === 'taxi');
  // A NEW SQUAD TAKES THE SEASONS the sport's own preset gives one, rookies
  // only where none has.
  const taxiSeasons =
    sport.rosterPresets.find((p) => p.taxi !== null)?.taxi?.maxYearsExperience ?? 0;
  const taxiRule = taxi?.rule ?? null;
  return (
    <Stack gap={3}>
      <Caption>
        Slots · {starters} starters · {total} roster spots
      </Caption>
      <div className="fsp-form-slots">
        {sport.slots
          .filter((sl) => sl.kind === 'starter' || sl.kind === 'bench')
          .filter(
            (sl) =>
              offered === undefined ||
              offered.includes(sl.id) ||
              (roster.slots.find((x) => x.slot === sl.id)?.count ?? 0) > 0,
          )
          .map((sl) => (
            <NumberField
              key={sl.id}
              label={sl.name}
              description={
                sl.kind === 'starter' && sl.eligible.length > 1 ? sl.eligible.join('/') : undefined
              }
              value={roster.slots.find((x) => x.slot === sl.id)?.count ?? 0}
              min={0}
              max={20}
              // INSIDE WHAT A ROSTER HOLDS while somebody types (`RosterConfig`
              // caps a slot at 20): a number past it never reaches the rules.
              onChange={(v) => onSlot(sl.id, Math.max(0, Math.min(20, Math.trunc(v ?? 0))))}
              disabled={disabled}
            />
          ))}
      </div>
      {reserve ? (
        <div className="fsp-form-fields">
          <NumberField
            label={`${reserve.name} spots`}
            description="For injured players; they must move off when healthy."
            value={roster.reserve}
            min={0}
            max={10}
            onChange={(v) => onReserve(Math.max(0, Math.min(10, Math.trunc(v ?? 0))))}
            disabled={disabled}
          />
        </div>
      ) : null}
      {taxi !== undefined && taxiSlot !== undefined ? (
        <div className="fsp-form-fields">
          <NumberField
            label={`${taxiSlot.name} spots`}
            description="For young players; they count toward no limit and never start."
            value={taxiRule?.count ?? 0}
            min={0}
            max={10}
            onChange={(v) => {
              // ZERO TO TEN, Sleeper's range.
              const count = Math.max(0, Math.min(10, Math.trunc(v ?? 0)));
              taxi.onChange(
                count === 0
                  ? null
                  : {
                      count,
                      maxYearsExperience: taxiRule?.maxYearsExperience ?? taxiSeasons,
                      deadlinePeriod: taxiRule?.deadlinePeriod ?? null,
                    },
              );
            }}
            disabled={disabled}
          />
          {taxiRule !== null ? (
            <>
              <NumberField
                label="Most seasons in the league"
                description="0 takes rookies only."
                value={taxiRule.maxYearsExperience}
                min={0}
                max={5}
                onChange={(v) =>
                  taxi.onChange({
                    ...taxiRule,
                    maxYearsExperience: Math.max(0, Math.min(5, Math.trunc(v ?? 0))),
                  })
                }
                disabled={disabled}
              />
              <Choice
                label={`${taxiSlot.name} squad closes`}
                description="Nobody goes on after that kickoff; a player can always come off."
                searchable={false}
                required
                value={taxiRule.deadlinePeriod === null ? 'first' : String(taxiRule.deadlinePeriod)}
                onChange={(v) =>
                  v &&
                  taxi.onChange({
                    ...taxiRule,
                    deadlinePeriod: v === 'first' ? null : Number(v),
                  })
                }
                options={[
                  { value: 'first', label: 'The season’s first kickoff' },
                  ...Array.from(
                    { length: Math.max(0, taxi.weeks.last - taxi.weeks.first) },
                    (_, i) => taxi.weeks.first + i + 1,
                  ).map((week) => ({
                    value: String(week),
                    label: `Week ${String(week)}’s first kickoff`,
                  })),
                  // A WEEK THE SEASON NO LONGER REACHES stays chosen, for the
                  // validator to name, rather than vanishing from the field.
                  ...(taxiRule.deadlinePeriod !== null &&
                  (taxiRule.deadlinePeriod <= taxi.weeks.first ||
                    taxiRule.deadlinePeriod > taxi.weeks.last)
                    ? [
                        {
                          value: String(taxiRule.deadlinePeriod),
                          label: `Week ${String(taxiRule.deadlinePeriod)}’s first kickoff`,
                        },
                      ]
                    : []),
                ]}
                disabled={disabled}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </Stack>
  );
}
